import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLocationsData, Location } from '../hooks/useLocationsData';
import { LocationsList } from '../components/MapsLocationsList';
import { MapContainer } from '../components/MapsMapContainer';

export const MapsSection = () => {
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  
  const {
    categories,
    locations,
    loading,
    error,
    getLocationsByCategory,
    getVisibleLocations
  } = useLocationsData();

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
  };

  const visibleLocations = getVisibleLocations();

  if (loading) {
    return (
      <section id="maps" className="min-h-screen py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h2 className="font-script text-4xl sm:text-5xl font-bold text-center text-secondary mb-12">
              Nuestros Lugares
            </h2>
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <span className="ml-4 text-text-light">Cargando nuestros lugares...</span>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="maps" className="min-h-screen py-20 sm:py-32">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h2 className="font-script text-4xl sm:text-5xl font-bold text-center text-secondary mb-12">
              Nuestros Lugares Especiales
            </h2>
            <div className="bg-red-100/20 backdrop-blur-xl rounded-2xl p-8">
              <p className="text-red-400 mb-4">Error cargando ubicaciones</p>
              <p className="text-sm text-text-light">{error}</p>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  if (categories.length === 0 || visibleLocations.length === 0) {
    return (
      <section id="maps" className="min-h-full py-4 px-4">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h2 className="font-script text-4xl sm:text-5xl font-bold text-center text-secondary mb-12">
              Nuestros Lugares
            </h2>
            <div className="bg-white/20 backdrop-blur-xl rounded-2xl shadow-2xl p-12">
              <div className="text-6xl mb-6">🗺️</div>
              <h3 className="text-2xl font-bold text-text-main mb-4">
                Próximamente nuestros lugares
              </h3>
              <p className="text-text-light max-w-md mx-auto">
                Estoy preparando un mapa interactivo con todos los lugares que hemos visitado juntos.
                ¡Regresa pronto para explorar nuestras aventuras!
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="maps" className="min-h-screen py-20 sm:py-32">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Header */}
          <div className="text-center">
            <h2 className="font-script text-4xl sm:text-5xl font-bold text-secondary mb-4">
              Nuestros Lugares
            </h2>
            <p className="text-secondary max-w-2xl mx-auto">
              Un mapa de todos los lugares que hemos visitado juntos, recuerdos y momentos únicos.
            </p>
            <div className="flex justify-center items-center gap-6 mt-6 text-sm text-text-light">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-text-light rounded-full"></div>
                <span>{categories.length} categorías</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-secondary rounded-full"></div>
                <span>{visibleLocations.length} lugares</span>
              </div>
              {selectedLocation && (
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span>Ubicación seleccionada</span>
                </div>
              )}
            </div>
          </div>

          {/* Contenido Principal */}
          <div className="flex flex-col gap-4">
            {/* Lista de Ubicaciones - Columna derecha en desktop lg:col-span-2 lg:order-1 */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="lg:order-2"
            >
              <LocationsList
                categories={categories}
                locations={visibleLocations}
                onLocationSelect={handleLocationSelect}
                selectedLocationId={selectedLocation?.id}
                getLocationsByCategory={getLocationsByCategory}
              />
            </motion.div>

            {/* Mapa - Columnas izquierda en desktop grid grid-cols-1 lg:grid-cols-3 gap-8 h-[700px] */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="lg:col-span-2 lg:order-1"
            >
              <MapContainer
                selectedLocation={selectedLocation}
                className="h-full"
              />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};